// API request/response Zod schemas referenced by sub-plans 04-08 (Workers
// handlers) and consumed by sub-plans 09-14 (mobile + admin clients). Each
// schema is the SERVER-SIDE validation gate for its endpoint; clients reuse
// the inferred TS types via `z.infer<typeof X>` at the call site.
//
// Response-shape policy (sub-plan 03 line 106 — R23): only REQUEST payloads
// get dedicated `*Req` schemas here. Response shapes are derived from
// table-Zod models (`UserProfile`, `Job`, `Rating`, etc. in ./tables) at the
// consumer site. Exceptions are response shapes that DON'T map 1:1 to a
// table row — those get explicit `*Res` exports below
// (`AdminBroadcastRes`, `ResetPasswordByTokenRes`).

import { z } from "zod";
import {
  engineerQualLevel,
  jobStatus,
  notificationKind,
  ticketStatus,
  otpPurpose,
  adminRoleLabel,
  adminBroadcastPushKind,
  adminBroadcastUserGroup,
  language,
} from "./enums";

// ---------------------------------------------------------------- Auth ---

// Phone-OTP send. `purpose` is a CLIENT-ONLY hint: clients record it to
// decide what screen to navigate to next. The Workers API picks an SMS
// template per purpose when the direct-Twilio path is active (i18n SMS
// sub-plan); when falling back to Supabase Auth, purpose is unused.
export const PhoneStartOtpReq = z.object({
  phone: z.string().regex(/^\+\d{7,15}$/),
  purpose: z.enum(otpPurpose),
  // Optional. When the direct-Twilio path is active, the SMS body is rendered
  // from `sms.<otp_template>` keyed by purpose, in this language. Recipient
  // doesn't have a user_profiles row yet (pre-registration/login/reset), so
  // we accept it as a hint. When omitted, defaults to "en".
  language: z.enum(language).optional(),
});
export type PhoneStartOtpReq = z.infer<typeof PhoneStartOtpReq>;

export const PhoneVerifyOtpReq = z.object({
  phone: z.string(),
  code: z.string().length(6),
});
export type PhoneVerifyOtpReq = z.infer<typeof PhoneVerifyOtpReq>;

// Password login — phone-only login (no OTP factor on the mobile login path
// for first-edition simplicity). The `otp_proof_token` field is kept on the
// schema for back-compat with older clients and for any future re-enable,
// but is no longer required when `phone` is supplied. .refine() chain
// enforces "exactly one of phone or email".
export const PasswordLoginReq = z
  .object({
    phone: z.string().optional(),
    // `email` accepts an email address OR a username (server resolves the
    // username via user_profiles.username → email before calling Supabase
    // Auth). Renamed semantically to "login id" but the wire field stays
    // `email` for back-compat with existing mobile builds.
    email: z.string().min(1).optional(),
    password: z.string().min(1),
    otp_proof_token: z.string().optional(),
  })
  .refine((d) => Boolean(d.phone) || Boolean(d.email), {
    message: "either phone or email is required",
  })
  .refine((d) => Boolean(d.phone) !== Boolean(d.email), {
    message: "provide exactly one of phone or email",
  });
export type PasswordLoginReq = z.infer<typeof PasswordLoginReq>;

// `identifier` is either an email or an HK-shaped phone in E.164.
// Server branches on `startsWith("+")` to choose the email vs phone path —
// without this refinement a malformed identifier would fall into the email
// branch and reach Resend's `to` field as garbage (Resend would 400).
const PhoneOrEmail = z
  .string()
  .min(1)
  .refine(
    (s) => /^\+\d{7,15}$/.test(s) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s),
    { message: "must be E.164 phone (+digits) or RFC-5321 email" },
  );

// identifier in this schema is intentionally looser than PhoneOrEmail —
// admin users sign in with a username (no '@'), so the forgot-password
// flow must accept it too. The /forgot-password and /reset-password
// handlers resolve a non-email-non-phone identifier against
// user_profiles.username before dispatching to the email path.
const ResetIdentifier = z.string().min(1).max(254);

export const ForgotPasswordReq = z.object({
  identifier: ResetIdentifier,
  // Optional language hint for the reset-code email subject + body. Server
  // prefers user_profiles.preferred_language (looked up by email) when the
  // recipient is registered; falls back to this field for unregistered
  // identifiers. Older mobile builds omit this field. (Sub-plan 05 §5.5).
  language: z.enum(language).optional(),
});
export type ForgotPasswordReq = z.infer<typeof ForgotPasswordReq>;

// Phone+email-OTP registration. Both customers and engineers use the same
// form: first_name + last_name + email + phone + password. email is
// required for both roles — receipts, payment and withdrawal records, and
// account recovery flow through it. Report §8.2.3 ("Email verification")
// is enforced via a separate /v1/auth/email-otp/{start,verify} pair that
// issues `email_otp_proof_token`; /register requires BOTH that proof and
// the phone `otp_proof_token` so a stolen phone-only proof can't create
// accounts under arbitrary email addresses.
//
// Engineers complete their profile (work experience, bio, service areas)
// via the post-signup wizard which calls PATCH /v1/engineer/me.
// display_name is auto-derived as "first_name last_name" by the API for
// engineers; customers may either supply display_name or rely on the same
// auto-derivation.
export const RegisterReq = z.object({
  phone: z.string().regex(/^\+\d{7,15}$/),
  password: z.string().min(8),
  role: z.enum(["customer", "engineer"]),
  first_name: z.string().min(1).max(40),
  last_name: z.string().min(1).max(40),
  display_name: z.string().min(1).max(80).optional(),
  email: z.string().email().max(254),
  otp_proof_token: z.string(),
  email_otp_proof_token: z.string(),
  // Optional: i18n sub-plan 02. Mobile builds with language switching send
  // their current local language; older builds omit it and fall back to the
  // user_profiles.preferred_language column DEFAULT 'en'.
  language: z.enum(language).optional(),
});
export type RegisterReq = z.infer<typeof RegisterReq>;

// Email-OTP — mirrors the phone OTP pair (see /v1/auth/otp/{start,verify}).
// Used at registration: caller verifies they own the email by entering a
// 6-digit code we mail via Resend, then exchanges the code for an
// email_otp_proof_token JWT that /register accepts alongside the phone
// otp_proof_token.
export const EmailStartOtpReq = z.object({
  email: z.string().email().max(254),
  purpose: z.enum(["register"]),
  // Optional language hint for the verification-code email. The recipient
  // doesn't have a user_profiles row yet (this fires PRE-registration), so
  // we can't look up preferred_language. Falls back to "en" when omitted.
  // (Sub-plan 05 §5.5).
  language: z.enum(language).optional(),
});
export type EmailStartOtpReq = z.infer<typeof EmailStartOtpReq>;

export const EmailVerifyOtpReq = z.object({
  email: z.string().email().max(254),
  code: z.string().regex(/^\d{6}$/),
});
export type EmailVerifyOtpReq = z.infer<typeof EmailVerifyOtpReq>;

// Logout — device-local when expo_token is provided; all-devices otherwise.
// `.nullish()` (NOT just `.optional()`) so clients that serialize empty as
// `null` (`JSON.stringify({expo_token: null})`) don't 400.
export const LogoutReq = z.object({
  expo_token: z.string().nullish(),
});
export type LogoutReq = z.infer<typeof LogoutReq>;

// Authenticated password change. Requires CURRENT password so a session
// compromise alone can't silently rotate the password.
export const ChangePasswordReq = z.object({
  old_password: z.string().min(1),
  new_password: z.string().min(8),
});
export type ChangePasswordReq = z.infer<typeof ChangePasswordReq>;

// PATCH /v1/me/language — authenticated. Caller sends the new language code;
// server validates against the `language` enum and writes it to
// user_profiles.preferred_language. Idempotent. (Sub-plan 02 + 04).
export const SetLanguageReq = z.object({
  language: z.enum(language),
});
export type SetLanguageReq = z.infer<typeof SetLanguageReq>;

// Admin overriding another admin's password (TVP2 §9 inline reset). Skips
// the old-password verification — gated by can_manage_admins instead.
// MUST NOT be used on self (use /me/password which forces old_password).
export const SetAdminPasswordReq = z.object({
  new_password: z.string().min(8),
});
export type SetAdminPasswordReq = z.infer<typeof SetAdminPasswordReq>;

// Same shape, but for the app-user set-password path. Same {new_password}
// envelope keeps wire-side parsers boring; gated by can_manage_app_users
// instead and operates on customer/engineer rows.
export const SetAppUserPasswordReq = SetAdminPasswordReq;
export type SetAppUserPasswordReq = z.infer<typeof SetAppUserPasswordReq>;

// Admin create-app-user. Mirrors CreateAdminUserReq: username + name parts
// required, password optional (server auto-generates a strong one and
// returns it once when omitted). Role distinguishes customer vs engineer
// — server creates the matching profile row.
//
// Phone is REQUIRED — the mobile app uses phone as the login identifier
// for the OTP-based register/login flows (apps/api/src/routes/auth.ts
// /register expects body.phone, and the SMS OTP gate is keyed by phone).
// Allowing an admin-created app user without a phone would leave them
// unable to sign in from the mobile app.
export const CreateAppUserReq = z
  .object({
    role: z.enum(["customer", "engineer"]),
    username: z
      .string()
      .min(3)
      .max(50)
      .regex(/^[A-Za-z0-9._-]+$/, "letters, numbers, '.', '_', '-' only"),
    email: z.string().email().max(254),
    first_name: z.string().min(1).max(80),
    last_name: z.string().min(1).max(80),
    phone: z.string().regex(/^\+\d{7,15}$/, {
      message: "phone must be E.164 (e.g. +85255551234)",
    }),
    password: z.string().min(8).optional(),
  });
export type CreateAppUserReq = z.infer<typeof CreateAppUserReq>;

// Password reset. Two factor variants:
//   - `otp_code`         (legacy single-screen flow): raw 6-digit SMS code
//                        verified server-side at the same call.
//   - `otp_proof_token`  (two-step mobile flow): JWT-signed proof issued
//                        by /v1/auth/otp/verify after the engineer entered
//                        their code on a dedicated screen. The reset-
//                        password screen now consumes the proof.
// Exactly one of the two MUST be present.
export const ResetPasswordReq = z
  .object({
    // Same relaxed shape as ForgotPasswordReq — admin usernames lack '@',
    // and the handler resolves them via user_profiles.username before
    // checking the KV `reset-code:{email}` bucket.
    identifier: ResetIdentifier,
    new_password: z.string().min(8),
    otp_code: z.string().length(6).optional(),
    otp_proof_token: z.string().optional(),
  })
  .refine(
    (d) => Boolean(d.otp_code) !== Boolean(d.otp_proof_token),
    { message: "Provide exactly one of otp_code or otp_proof_token" },
  );
export type ResetPasswordReq = z.infer<typeof ResetPasswordReq>;

// Magic-link reset (R55+ — admin-initiated reset for app users via
// dual-host email). The UUID token from the email IS the auth factor.
export const ResetPasswordByTokenReq = z.object({
  token: z.string().uuid(),
  new_password: z.string().min(8),
});
export type ResetPasswordByTokenReq = z.infer<typeof ResetPasswordByTokenReq>;

// Response of POST /v1/auth/reset-password-by-token. Admin web A05b reads
// `target_role` to branch redirect; mobile reset.tsx uses it to show
// role-specific success copy.
export const ResetPasswordByTokenRes = z.object({
  ok: z.literal(true),
  target_role: z.enum(["admin", "customer", "engineer"]),
});
export type ResetPasswordByTokenRes = z.infer<typeof ResetPasswordByTokenRes>;

// Push-token registration (R22 — POST /v1/me/push-token).
// Platform.OS may return 'web'|'macos'|'windows' on non-mobile clients;
// callers MUST normalize to 'ios'|'android' before send (others 400 here).
export const PushTokenReq = z.object({
  expo_token: z.string().min(1),
  platform: z.enum(["ios", "android"]),
});
export type PushTokenReq = z.infer<typeof PushTokenReq>;

// ---------------------------------------------------------- Tickets ----

// Ticket creation (R28 — sub-plan 05 + sub-plan 06 ticket POST).
// Char caps mirror tickets.subject (200) + body (5000) schema CHECKs.
export const CreateTicketReq = z.object({
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(5000),
  rating: z.number().int().min(1).max(5).optional(),
});
export type CreateTicketReq = z.infer<typeof CreateTicketReq>;

// Admin PATCH /v1/admin/tickets/:id (sub-plan 07 line 119). `.strict()`
// rejects unknown keys (defense against direct PostgREST writes).
export const PatchTicketReq = z
  .object({
    status: z.enum(ticketStatus).optional(),
    assigned_to: z.string().uuid().nullable().optional(),
    resolved_at: z.string().datetime().nullable().optional(),
  })
  .strict();
export type PatchTicketReq = z.infer<typeof PatchTicketReq>;

// ---------------------------------------------------------- Addresses ---

// Field caps mirror addresses.* CHECK constraints (sub-plan 02 §5).
export const CreateAddressReq = z.object({
  label: z.string().trim().max(40).optional(),
  street: z.string().trim().min(1).max(200),
  apt: z.string().trim().max(60).optional(),
  district: z.string().trim().max(60).optional(),
  building: z.string().trim().max(100).optional(),
  region: z.string().trim().max(60).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});
export type CreateAddressReq = z.infer<typeof CreateAddressReq>;

export const UpdateAddressReq = CreateAddressReq.partial();
export type UpdateAddressReq = z.infer<typeof UpdateAddressReq>;

// ---------------------------------------------------------- Profile ----

// R30 — both schemas use `.strict()` to reject unknown keys (e.g. attempts
// to write admin-controlled columns through PATCH /me). Per-field caps
// mirror sub-plan 02 schema CHECK constraints.
//
// `email_otp_proof_token` — when the patch contains a NEW email, the server
// requires this proof (issued by /v1/auth/email-otp/verify against the new
// address). At the schema layer the field is optional because patches that
// don't change email don't need it; the handler enforces presence at runtime.
export const UpdateCustomerProfileReq = z
  .object({
    display_name: z.string().min(1).max(80).optional(),
    email: z.string().email().max(254).optional(),
    phone: z
      .string()
      .regex(/^\+\d{7,15}$/)
      .optional(),
    avatar_url: z.string().url().optional(),
    email_otp_proof_token: z.string().optional(),
  })
  .strict();
export type UpdateCustomerProfileReq = z.infer<typeof UpdateCustomerProfileReq>;

export const UpdateEngineerProfileReq = z
  .object({
    display_name: z.string().min(1).max(80).optional(),
    first_name: z.string().min(1).max(40).optional(),
    last_name: z.string().min(1).max(40).optional(),
    email: z.string().email().max(254).optional(),
    phone: z
      .string()
      .regex(/^\+\d{7,15}$/)
      .optional(),
    avatar_url: z.string().url().optional(),
    bio: z.string().max(2000).optional(),
    work_experience_years: z.number().int().min(0).max(70).optional(),
    // Migration 00013 — multi-select HK 18 districts. Wizard step 1 saves
    // this; engineer can edit later from profile.
    service_areas: z.array(z.string().max(60)).max(18).optional(),
    email_otp_proof_token: z.string().optional(),
  })
  .strict();
export type UpdateEngineerProfileReq = z.infer<typeof UpdateEngineerProfileReq>;

// Engineer tax/payment info — admin-only, encrypted at rest (migration 00032).
// Plaintext shape is the JSON stored inside engineer_profiles.tax_info_enc.
// HKID format: 1 letter + 6 digits + 1 check char in parens, e.g. "A123456(7)".
// We accept either "A123456(7)" or the looser "A1234567" admins copy from
// scans; normalisation is the admin's responsibility.
export const EngineerTaxInfo = z.object({
  legal_name: z.string().min(1).max(120).nullable(),
  hkid: z
    .string()
    .max(20)
    .regex(/^[A-Z]{1,2}\d{6}\(?[0-9A]\)?$/i)
    .nullable(),
  home_address: z.string().min(1).max(500).nullable(),
  date_of_birth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/) // ISO YYYY-MM-DD
    .nullable(),
});
export type EngineerTaxInfo = z.infer<typeof EngineerTaxInfo>;

// Admin write — every field optional so admin can update one at a time.
// Server merges into the existing decrypted payload before re-encrypting.
export const UpdateEngineerTaxInfoReq = z
  .object({
    legal_name: z.string().min(1).max(120).nullable().optional(),
    hkid: z
      .string()
      .max(20)
      .regex(/^[A-Z]{1,2}\d{6}\(?[0-9A]\)?$/i)
      .nullable()
      .optional(),
    home_address: z.string().min(1).max(500).nullable().optional(),
    date_of_birth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
  })
  .strict();
export type UpdateEngineerTaxInfoReq = z.infer<typeof UpdateEngineerTaxInfoReq>;

// Decrypted bank info from the most recent withdrawal_request, surfaced on
// the admin engineer-detail page so admins don't have to dig into the
// withdrawals queue. Mirrors destination_details_enc payload shape.
export const EngineerLatestBankInfo = z.object({
  bank_name: z.string().nullable(),
  account_holder: z.string().nullable(),
  account_number: z.string().nullable(),
  // ISO date of the withdrawal that captured this — helps admin gauge
  // staleness ("collected 2024-08-12") without opening the withdrawals page.
  captured_at: z.string(),
});
export type EngineerLatestBankInfo = z.infer<typeof EngineerLatestBankInfo>;

// ---------------------------------------------------------- Jobs -------

// Customer creates a job (sub-plan 05 §5.x). Up to 10 photos, matches
// engineer-completion cap.
export const CreateJobReq = z.object({
  address_id: z.string().uuid(),
  category_id: z.string().uuid(),
  description: z.string().min(1).max(4000),
  required_qualification: z.enum(engineerQualLevel),
  photo_paths: z.array(z.string()).max(10).optional(),
  preferred_engineer_id: z.string().uuid().optional(),
  scheduled_at: z.string().datetime().optional(),
});
export type CreateJobReq = z.infer<typeof CreateJobReq>;

// Customer PATCH /v1/customer/jobs/:id (sub-plan 05 §5.x). Intentionally
// narrow — only fields a customer can edit BEFORE engineer accept. `status`
// is FSM-controlled (use /cancel or wait for engineer FSM transitions), and
// admin-controlled fields (engineer_id, price_amount) are explicitly excluded.
// `.strict()` rejects unknown keys to defend against direct PostgREST writes.
export const PatchCustomerJobReq = z
  .object({
    description: z.string().trim().min(1).max(2000).optional(),
    preferred_engineer_id: z.string().uuid().optional().nullable(),
    scheduled_at: z.string().datetime().optional().nullable(),
  })
  .strict();
export type PatchCustomerJobReq = z.infer<typeof PatchCustomerJobReq>;

// Customer → engineer rating (M22 Evaluate, invoice 7.4.8). Three sub
// ratings; `overall` is server-computed = round((a+b+c)/3, 1).
export const CustomerRateEngineerReq = z.object({
  response_speed: z.number().min(1).max(5),
  service_attitude: z.number().min(1).max(5),
  engineer_rating: z.number().min(1).max(5),
  review: z.string().max(2000).optional(),
});
export type CustomerRateEngineerReq = z.infer<typeof CustomerRateEngineerReq>;

// Engineer → customer rating (invoice 8.5.8). Single 1-5 star.
export const EngineerRateCustomerReq = z.object({
  rating: z.number().min(1).max(5),
  remarks: z.string().max(2000).optional(),
});
export type EngineerRateCustomerReq = z.infer<typeof EngineerRateCustomerReq>;

// Engineer completes a job (sub-plan 06 §6.5). >=1 photo (proof), <=10.
// Optional lat/lng captured by the engineer mobile right before submit
// — saved on the job as service_completed_* for ops audit.
//
// photo_paths is OPTIONAL — many job categories don't have anything
// photo-worthy to capture (printer driver fix, email migration, remote
// support). Customer still confirms via the in-app workflow either way.
// Cap at 10 to keep the storage cost predictable.
export const CompleteJobReq = z.object({
  photo_paths: z.array(z.string()).max(10).optional().default([]),
  remarks: z.string().max(2000).optional(),
  price_amount: z.number().nonnegative().max(1_000_000).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});
export type CompleteJobReq = z.infer<typeof CompleteJobReq>;

// Engineer release-job body (R29 — sub-plan 06 §6.6a). Reason stored in
// job_status_history.note.
export const ReleaseJobReq = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type ReleaseJobReq = z.infer<typeof ReleaseJobReq>;

// ---------------------------------------------------------- PaymentMethod

// Customer payment-method create/update body (sub-plan 05 §5.x — POST/PATCH
// /v1/customer/payment-methods). Mirrors `payment_methods` table columns
// (sub-plan 02 §6, init.sql lines 169-182). `card_plain` is the phase-1
// INSECURE placeholder column (per sub-plan 02 line 220 / init.sql line 178)
// — handler accepts it on input but the response NEVER echoes it back
// (handler reselects via PM_FIELDS which omits the column). Field caps are
// permissive but bounded to keep accidental large-blob writes out.
export const PaymentMethodReq = z.object({
  kind: z.enum(["credit_card", "bank_transfer", "cash"]),
  label: z.string().trim().min(1).max(120).optional().nullable(),
  card_last4: z
    .string()
    .regex(/^\d{4}$/)
    .optional()
    .nullable(),
  card_brand: z.string().trim().min(1).max(40).optional().nullable(),
  card_plain: z.string().min(1).max(40).optional().nullable(),
  is_default: z.boolean().optional(),
});
export type PaymentMethodReq = z.infer<typeof PaymentMethodReq>;

export const UpdatePaymentMethodReq = PaymentMethodReq.partial();
export type UpdatePaymentMethodReq = z.infer<typeof UpdatePaymentMethodReq>;

// ---------------------------------------------------------- Pagination -

// Cursor pagination (protocol A — mobile + most read endpoints).
export const PaginationQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type PaginationQuery = z.infer<typeof PaginationQuery>;

// Offset pagination (protocol B — admin DataGrid).
export const OffsetPaginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type OffsetPaginationQuery = z.infer<typeof OffsetPaginationQuery>;

// String → boolean transform helper. Avoids the z.coerce.boolean()
// "false"→true footgun (any non-empty string coerces to true).
export const queryBool = z
  .enum(["true", "false"])
  .transform((s) => s === "true");

// ---------------------------------------------------------- Listings ---

// Customer "my jobs" (sub-plan 05). Sort options drive M21 list ordering.
// `dir` lets the JOB REQUEST list flip ascending/descending — re-tapping
// the active sort chip in the mobile UI toggles this.
export const ListCustomerJobsQuery = z.object({
  sort: z.enum(["date", "service_time", "review"]).default("date"),
  dir: z.enum(["asc", "desc"]).default("desc"),
  status: z.enum(jobStatus).optional(),
  type: z.string().uuid().optional(),
  date_from: z.string().date().optional(),
  date_to: z.string().date().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ListCustomerJobsQuery = z.infer<typeof ListCustomerJobsQuery>;

// Engineer history (sub-plan 06 §6.1 / E12 history screen).
export const ListEngineerHistoryJobsQuery = z.object({
  sort: z.enum(["date", "service_time", "review"]).default("date"),
  status: z.enum(jobStatus).optional(),
  type: z.string().uuid().optional(),
  date_from: z.string().date().optional(),
  date_to: z.string().date().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ListEngineerHistoryJobsQuery = z.infer<
  typeof ListEngineerHistoryJobsQuery
>;

// Admin "all jobs" — offset paginated.
export const ListAdminJobsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: z.enum(jobStatus).optional(),
  q: z.string().optional(),
});
export type ListAdminJobsQuery = z.infer<typeof ListAdminJobsQuery>;

// Admin "all app users" — offset paginated.
export const ListAppUsersQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  role: z.enum(["customer", "engineer"]).optional(),
  q: z.string().optional(),
});
export type ListAppUsersQuery = z.infer<typeof ListAppUsersQuery>;

// Admin "list admin users" — offset paginated. Sub-plan 07 §7.1 row 1:
// supports `is_active` filter (boolean — admins have no approval lifecycle, only
// soft-delete via `is_active=false`). The queryBool transform avoids the
// z.coerce.boolean() footgun where any non-empty string ("false") coerces to true.
export const ListAdminUsersQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  q: z.string().optional(),
  is_active: queryBool.optional(),
});
export type ListAdminUsersQuery = z.infer<typeof ListAdminUsersQuery>;

// Admin tickets list — offset paginated, optional status filter.
// `kind` lets the admin tickets list page exclude disputes (which have their
// own /admin/disputes view). Allowed values match the DB CHECK constraint
// on tickets.ticket_kind (migrations 00020b / 00023).
export const ListTicketsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: z.enum(ticketStatus).optional(),
  assigned_to: z.string().uuid().optional(),
  kind: z.enum(["general", "dispute", "feedback"]).optional(),
  exclude_kind: z.enum(["general", "dispute", "feedback"]).optional(),
});
export type ListTicketsQuery = z.infer<typeof ListTicketsQuery>;

// Admin ticket-question-templates list (admin Question template tab).
export const ListTicketQuestionTemplatesQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  category: z.string().optional(),
});
export type ListTicketQuestionTemplatesQuery = z.infer<
  typeof ListTicketQuestionTemplatesQuery
>;

// Admin job-categories list.
export const ListJobCategoriesQuery = z.object({
  is_active: queryBool.optional(),
  q: z.string().optional(),
});
export type ListJobCategoriesQuery = z.infer<typeof ListJobCategoriesQuery>;

// ---------------------------------------------------------- Admin CRUD -

// Admin user role label string (admin "Change user permission"). Mirrors
// admin_permissions.role_label CHECK.
export const RoleLabel = z.enum(adminRoleLabel);
export type RoleLabel = z.infer<typeof RoleLabel>;

// Partial permissions write body — admin PATCH endpoints accept any subset
// of the 5 permission booleans + role_label.
export const AdminPermissionsPartial = z
  .object({
    can_manage_admins: z.boolean().optional(),
    can_manage_app_users: z.boolean().optional(),
    can_manage_jobs: z.boolean().optional(),
    can_send_push: z.boolean().optional(),
    can_view_reports: z.boolean().optional(),
    role_label: RoleLabel.optional(),
  })
  .strict();
export type AdminPermissionsPartial = z.infer<typeof AdminPermissionsPartial>;

// Migration 00011 adds first_name + last_name + username. Username is
// 3-50 chars, case-insensitive unique, alphanumeric + dot/underscore/hyphen.
const Username = z
  .string()
  .min(3)
  .max(50)
  .regex(/^[A-Za-z0-9._-]+$/, "letters, numbers, '.', '_', '-' only");

// Admin create-admin-user (sub-plan 07 §7.1).
// Either first_name OR display_name MUST be supplied — first_name wins
// when both are provided (the DB trigger overwrites display_name from
// first/last). username is required — it's the admin-facing login handle.
export const CreateAdminUserReq = z
  .object({
    email: z.string().email().max(254),
    username: Username,
    display_name: z.string().min(1).max(80).optional(),
    first_name: z.string().min(1).max(80).optional(),
    last_name: z.string().min(1).max(80).optional(),
    // E.164 — same shape as the rest of user_profiles.phone usage. Optional
    // because admins typically don't have a phone on file; the field exists
    // so the Add admin form mirrors the Edit form's layout.
    phone: z.string().regex(/^\+\d{7,15}$/).optional(),
    // password optional: when omitted, server generates a strong random
    // one and returns it in the response (shown to the creator ONCE).
    password: z.string().min(8).optional(),
    permissions: AdminPermissionsPartial.optional(),
  })
  .refine((v) => Boolean(v.first_name) || Boolean(v.display_name), {
    message: "first_name or display_name is required",
    path: ["first_name"],
  });
export type CreateAdminUserReq = z.infer<typeof CreateAdminUserReq>;

// Admin patch-admin-user. Per sub-plan 07 §7.1 line 181, `role_label` is a
// top-level field in the request body even though it lives on the
// admin_permissions table (not user_profiles); the handler merges it into the
// permissions UPDATE alongside any nested `permissions` patch.
// first_name, last_name and username added by migration 00011.
export const PatchAdminUserReq = z
  .object({
    display_name: z.string().min(1).max(80).optional(),
    first_name: z.string().min(1).max(80).optional(),
    last_name: z.string().min(1).max(80).optional(),
    username: Username.optional(),
    email: z.string().email().max(254).optional(),
    is_active: z.boolean().optional(),
    role_label: RoleLabel.optional(),
    permissions: AdminPermissionsPartial.optional(),
  })
  .strict();
export type PatchAdminUserReq = z.infer<typeof PatchAdminUserReq>;

// Admin role updates (migration 00012). Only the 5 boolean flags + the
// description can be edited via PATCH; `name` is read-only for system
// roles, and `is_system` / timestamps are server-controlled.
export const PatchAdminRoleReq = z
  .object({
    name: z.string().min(2).max(80).optional(),
    description: z.string().max(500).nullable().optional(),
    can_manage_admins: z.boolean().optional(),
    can_manage_app_users: z.boolean().optional(),
    can_manage_jobs: z.boolean().optional(),
    can_send_push: z.boolean().optional(),
    can_view_reports: z.boolean().optional(),
  })
  .strict();
export type PatchAdminRoleReq = z.infer<typeof PatchAdminRoleReq>;

// Admin GET /admin-users/me — composite response. Defined in tables.ts
// as `AdminUserMe`; re-exported under the `Resp` alias here for backwards
// compat with sub-plan 03 line 549. Single export (value + implicit type
// in one statement) avoids TS2300 duplicate-identifier.
export { AdminUserMe as AdminUserMeResp } from "./tables";

// ---------------------------------------------------------- Broadcasts -

// Admin email broadcast (A14). Server-side validation gate complementing
// A14's client-side disabled-Send.
// R7 N2 — `subject` rejects CR/LF/NUL chars to prevent SMTP header
// injection. Resend likely strips them server-side, but defense-in-depth:
// a malicious subject like `Subject: ...\r\nBcc: attacker@evil.com` could
// theoretically inject extra headers if any provider concatenates raw.
export const AdminSendEmailReq = z.object({
  user_group: z.enum(adminBroadcastUserGroup),
  subject: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[^\r\n\u0000]*$/, "subject must not contain control characters"),
  html: z.string().trim().min(1).max(100_000),
});
export type AdminSendEmailReq = z.infer<typeof AdminSendEmailReq>;

// Admin push broadcast (A13). `kind` is restricted to the 2 admin
// broadcastable kinds (NOT the full notificationKind enum) so admins can't
// inject `job_status`/`approval`/`billing`/`account` rows that would
// contaminate UI segregation in M27/E14 notification feeds.
// `body.max(178)` matches Expo's per-platform display limit;
// `title.max(60)` keeps lockscreen previews readable.
export const AdminSendPushReq = z.object({
  user_group: z.enum(adminBroadcastUserGroup),
  kind: z.enum(adminBroadcastPushKind).default("promotion"),
  title: z.string().trim().min(1).max(60),
  body: z.string().trim().min(1).max(178),
  data: z.record(z.string()).optional(),
});
export type AdminSendPushReq = z.infer<typeof AdminSendPushReq>;

// Shared broadcast response (R51 — `waitUntil` fan-out means admin UI
// surfaces "Broadcast queued" not "Sent").
export const AdminBroadcastRes = z.object({
  ok: z.literal(true),
  recipient_count: z.number().int().nonnegative(),
  status: z.literal("queued"),
});
export type AdminBroadcastRes = z.infer<typeof AdminBroadcastRes>;

// ------------------------------------------------ Notifications (S→C) -

export const NotificationPayload = z.object({
  kind: z.enum(notificationKind),
  title: z.string(),
  body: z.string().optional(),
  data: z.record(z.unknown()).optional(),
});
export type NotificationPayload = z.infer<typeof NotificationPayload>;
